import { observer, use$ } from "@legendapp/state/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { SceneControls } from "../../components/scene-controls/SceneControls";
import { SceneTester } from "../../components/scene-testesr/SceneTester";
import { useFullscreen } from "../../components/video/hooks/useFullscreen";
import { BigPlayPause } from "../../components/video/big-play-pause/BigPlayPause";
import { Overlays } from "../../components/video/Overlays/Overlays";
import { Track } from "../../components/video/track/Track";
import { VideoStage } from "../../components/video/VideoStage/VideoStage";
import {
  VideoPlayer,
  type VideoPlayerHandle,
} from "../../components/video/VideoPlayer/VideoPlayer";
import data from "../../data/navigation.json" assert { type: "json" };
import actionCalls from "../../services/actionCalls.ts";
import { globalStore$, setCurrentScreen } from "../../store/globalStore.ts";
import { Home } from "../home/Home";
import type {
  SceneCuePoint,
  SceneDefinition,
  SceneOverlay,
} from "../../types/navigation";
import isDevMode from "../../util/isDevMode";
import { getSubtitlesSource, getVideoSource } from "../../util/paths";
import "./scenes.css";

export const Scenes = observer(function () {
  const { sceneId } = useParams();
  const videoRef = useRef<VideoPlayerHandle>(null);
  const stageShellRef = useRef<HTMLDivElement>(null);
  const {
    isAvailable: isFullscreenAvailable,
    isFullscreen,
    enter: enterFullscreen,
    exit: exitFullscreen,
    toggle: toggleFullscreen,
  } = useFullscreen(stageShellRef);
  const autoFullscreenRef = useRef(false);
  const currentScreen = use$(globalStore$.currentScreen.get());
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [intendedPlaying, setIntendedPlaying] = useState(false);

  const scene: SceneDefinition | undefined = useMemo(
    () => data.scenes.find((s) => s.id === currentScreen) as SceneDefinition,
    [currentScreen],
  );

  const [currentOverlay, setCurrentOverlay] = useState<SceneOverlay | null>(
    null,
  );

  const [showSubtitles, setShowSubtitles] = useState(true);
  const hasStarted = globalStore$.hasStarted.get();

  const devMode: boolean = isDevMode();
  const subtitles: string = scene
    ? getSubtitlesSource(scene, data.season) || ""
    : "";

  const handleCue = (cue: SceneCuePoint) => {
    if (cue.params?.action?.name === "showOverlay") {
      if (!cue.params.action.id || !scene?.overlays) return;
      const overlayId = cue.params.action.id;
      const overlay: SceneOverlay = scene.overlays[overlayId];
      setCurrentOverlay(overlay);
    }
  };

  const changeScene = (sceneId: string) => {
    setCurrentScreen(sceneId);
    setCurrentOverlay(null);
    setIntendedPlaying(true);
  };

  const onActionCall = actionCalls({
    navigate: changeScene,
  });

  const toggleSubtitles = () => {
    setShowSubtitles((val) => !val);
  };

  const handleStart = () => {
    globalStore$.hasStarted.set(true);
    sessionStorage.setItem("hasStarted", "true");
    setIntendedPlaying(true);
    videoRef.current?.play();
  };

  const canTogglePlayback =
    hasStarted && !currentOverlay && !devMode && scene !== undefined;

  const showBigPlayPause =
    canTogglePlayback && !isVideoPlaying && !intendedPlaying;

  const handleTogglePlayback = () => {
    if (!canTogglePlayback) return;
    if (isVideoPlaying) {
      setIntendedPlaying(false);
      videoRef.current?.pause();
    } else {
      setIntendedPlaying(true);
      videoRef.current?.play();
    }
  };

  useEffect(() => {
    if (!canTogglePlayback) return;

    const isKeyboardActivatable = (element: Element | null) =>
      element instanceof HTMLElement &&
      (element.tagName === "BUTTON" ||
        element.tagName === "A" ||
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLSelectElement ||
        element.isContentEditable ||
        element.hasAttribute("tabindex"));

    const toggle = () => {
      if (isVideoPlaying) {
        setIntendedPlaying(false);
        videoRef.current?.pause();
      } else {
        setIntendedPlaying(true);
        videoRef.current?.play();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code !== "Space") return;
      if (event.repeat) return;
      const active = document.activeElement;
      if (isKeyboardActivatable(active)) return;
      event.preventDefault();
      toggle();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [canTogglePlayback, isVideoPlaying]);

  useEffect(() => {
    changeScene(sceneId || currentScreen);
  }, [sceneId]);

  useEffect(() => {
    if (!isFullscreenAvailable) return;

    const checkIsLandscape = () => {
      const angle = window.screen.orientation?.angle ?? window.orientation;
      if (typeof angle === "number" && angle !== 0) {
        return angle === 90 || angle === -90 || angle === 270;
      }
      // Fallback for Android and browsers without orientation API
      return window.innerWidth > window.innerHeight;
    };

    let orientationTimeout: ReturnType<typeof setTimeout> | null = null;

    const handleOrientationChange = () => {
      if (orientationTimeout) clearTimeout(orientationTimeout);
      // Wait a tick so Android reports updated innerWidth/innerHeight
      orientationTimeout = setTimeout(() => {
        const isLandscape = checkIsLandscape();

        if (isLandscape && !isFullscreen) {
          autoFullscreenRef.current = true;
          void enterFullscreen();
        } else if (
          !isLandscape &&
          isFullscreen &&
          autoFullscreenRef.current
        ) {
          autoFullscreenRef.current = false;
          void exitFullscreen();
        }
      }, 150);
    };

    window.addEventListener("orientationchange", handleOrientationChange);
    window.addEventListener("resize", handleOrientationChange);
    return () => {
      if (orientationTimeout) clearTimeout(orientationTimeout);
      window.removeEventListener("orientationchange", handleOrientationChange);
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, [isFullscreenAvailable, isFullscreen, enterFullscreen, exitFullscreen]);

  const handleToggleFullscreen = () => {
    autoFullscreenRef.current = false;
    void toggleFullscreen();
  };

  return (
    <div style={{ position: "relative" }}>
      <section id="scenesPage">
        {!hasStarted && <Home onStart={handleStart} />}

        {devMode && (
          <SceneTester
            sceneList={data.scenes as unknown as SceneDefinition[]}
            selectedScene={currentScreen}
            setSelectedScene={changeScene}
          />
        )}

        <div ref={stageShellRef} className="stage-shell">
          <div className="scene-controls-wrapper">
            <SceneControls
              key={currentScreen}
              showSubtitles={showSubtitles}
              toggleSubtitles={toggleSubtitles}
              isFullscreenAvailable={isFullscreenAvailable}
              isFullscreen={isFullscreen}
              toggleFullscreen={handleToggleFullscreen}
            />
          </div>
          <VideoStage>
            {scene && (
              <VideoPlayer
                key={scene.id}
                id={scene.id}
                ref={videoRef}
                src={getVideoSource(scene, data.season)}
                startTime={scene.startTime || 0}
                cuePoints={scene.cuePoints || []}
                onCue={handleCue}
                onComplete={scene.onComplete}
                showSubtitles={showSubtitles}
                changeScene={changeScene}
                onActionCall={onActionCall}
                onPlayingChange={setIsVideoPlaying}
                onPlayRejected={() => setIntendedPlaying(false)}
              >
                <Track subtitles={subtitles} />
              </VideoPlayer>
            )}
            {canTogglePlayback && (
              <BigPlayPause
                isPlaying={isVideoPlaying}
                onToggle={handleTogglePlayback}
                visible={showBigPlayPause}
              />
            )}
            {currentOverlay && (
              <Overlays
                id={currentOverlay.id}
                elements={currentOverlay.elements}
                fragmentId={currentOverlay.fragmentId}
                onActionCall={onActionCall}
                changeScene={changeScene}
              />
            )}
          </VideoStage>
        </div>
      </section>
    </div>
  );
});
